import React, { useMemo, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import AppBar from "../../../../../../../common/AppBar";
import {
  VersionHistoryPopup,
  getVersionHistoryAuditPreviewPath,
} from "../../../../../../../common/VersionHistoryPopup";
import { getAllVersionHistories } from "../../../../../../../redux/versionHistorySlice";
import ProcedureStatusInfo from "../../../RevisionPreview/ProcedureStatusInfo";
import SectionNode from "../../../../../../Documents/components/Nodes/SectionNode";
import StepNode from "../../../../../../Documents/components/Nodes/StepNode";
import SubstepNode from "../../../../../../Documents/components/Nodes/SubstepNode";
import AttachmentNode from "../../../../../../Documents/components/Nodes/AttachmentNode";
import VideoNode from "../../../../../../Documents/components/Nodes/VideoNode";
import RichTextNode from "../../../../../../Documents/components/Nodes/RichTextNode";
import CanvasNode from "../../../../../../Documents/components/Nodes/CanvasNode";
import RenderRevisionNodes from "../../../../../../Documents/components/RenderRevisionNodes";
import { dispatchAuditTrailNodeCopy } from "../../../../../utils/auditTrailDispatchNodeCopy";
import {
  auditTrailGeneralReviewPath,
  auditTrailNodeChangeReviewPath,
  isAuditTrailNodeChangeReviewEntry,
} from "../../../../utils/auditTrailNodeChangeReviewRoute";
import { resolveAuditTrailVersionId } from "../../../../../utils/auditTrailVersionIdResolve";
import VersionHistoryCopyReplace from "../../../../VersionHistoryCopyReplace/VersionHistoryCopyReplace";


const PreviewCommonComponent = ({previewData={},audit_trail_id}) => {

  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { versionHistories, loading: versionHistoryLoading } = useSelector(
    (state) => state.versionHistory,
  )
  const [copyLoading, setCopyLoading] = useState(false)
  const [showVersionHistory, setShowVersionHistory] = useState(false)

  const {
    auditTrailData,
    loading,
    error,
    snapshotLoading,
    snapshotError,
    versionContent,
    sections,
    resolved,
    checkboxState,
    displayTitle,
    hasSnapshotUrl,
    part_ref,
    procedure_rev_ref,
    isMainEntry=undefined,
    new_content_snapshot=undefined,
    actionToShow=undefined,
    cancel_screen_data=undefined,
    data_for_copy_and_replace=undefined

  } = previewData

  const isRevisionEntry = resolved?.type === 'revision'

  const resolvedAuditVersionId = useMemo(
    () => resolveAuditTrailVersionId(auditTrailData),
    [auditTrailData],
  )

  const [showCopyAndrelpace, setShowCopyAndrelpace] = useState(false)

  const nodeTitleForCopy = useMemo(() => {
    if (!displayTitle || displayTitle === 'Loading...') return ''
    const vid = resolvedAuditVersionId
    if (!vid) return displayTitle
    const stripped = displayTitle.replace(
      new RegExp(`\\s*\\(Version ${String(vid).replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\) Selected\\s*$`, 'i'),
      '',
    )
    return stripped.trim() || displayTitle
  }, [displayTitle, resolvedAuditVersionId])

  const handleTablesVersionHistory = useCallback(async () => {
    if (!resolvedAuditVersionId) return
    await dispatch(getAllVersionHistories({ versionId: resolvedAuditVersionId }))
    setShowVersionHistory(true)
  }, [dispatch, resolvedAuditVersionId])

  const handleCancel = () => {
    if(part_ref && procedure_rev_ref) {
      navigate(
        `/revision-submission/${part_ref}/${versionContent?.revision}/${versionContent?.item_code_type}/${versionContent?.partNumber}/${procedure_rev_ref}/redirect`,
        {
          state:{
            fromAuditTrail: true,
            main_entry:isMainEntry,
            auditTrailBackId: audit_trail_id,
            auditTrailSavedAtIso: auditTrailData?.audit_created_at,
            cancel_screen_data
          }
        }
      )
    } 
  }
  const handleReviewClick = () => {
    if (!audit_trail_id) return
    const path = isAuditTrailNodeChangeReviewEntry(auditTrailData)
      ? auditTrailNodeChangeReviewPath(audit_trail_id)
      : auditTrailGeneralReviewPath(audit_trail_id)
    navigate(path)
  }
  const handleViewAuditTrail = () => {
    if (audit_trail_id) navigate(`/audit-trail/${audit_trail_id}`)
  }
  const handleCopy = useCallback(async () => {
    if (!auditTrailData || copyLoading) return
    setCopyLoading(true)
    try {
      // Extract breadcrumb text for copy operation
      if(data_for_copy_and_replace && data_for_copy_and_replace.canPerformCopyAndReplace){
        setShowCopyAndrelpace(true)
      }else{
        let breadcrumbText = null;
        
        
        // Try to use globally stored breadcrumb from RevisionPreview first
        if (window.lastExtractedBreadcrumb) {
          breadcrumbText = window.lastExtractedBreadcrumb;
        } else {
        }
        
        await dispatchAuditTrailNodeCopy({
          dispatch,
          auditTrailId: audit_trail_id,
          revData: auditTrailData,
          nodeTitleOverride: nodeTitleForCopy,
          resolved,
          snapshotEnrichedIdentity: null,
          breadcrumbText,
        })
      }
    } finally {
      setCopyLoading(false)
    }
  }, [
    auditTrailData,
    audit_trail_id,
    copyLoading,
    dispatch,
    nodeTitleForCopy,
    resolved,
  ])

  const previewGlobalStepNumber =
    typeof resolved.displayGlobalStepNumber === 'number'
      ? resolved.displayGlobalStepNumber
      : typeof resolved.stepIndex === 'number' && resolved.stepIndex >= 0
        ? resolved.globalStepStart + resolved.stepIndex + 1
        : undefined

  let copyButton = { label: 'Copy', onClick: handleCopy, loading: copyLoading };
  if(isRevisionEntry) {
    copyButton.label = "Copy & Replace"
  }

  if(checkboxState !== null){
    checkboxState.allChildrenDisabled = true;
  }
  return (
    <> 
      { !showCopyAndrelpace && 
        <div>
          <AppBar
            title={displayTitle}
            titleFontSize="18px"
            showTablesDropdown={true}
            versionHistoryHandler={auditTrailData?.version_id ? handleTablesVersionHistory : null}
            showCancelButton={true}
            onCancel={handleCancel}
            showBackButton={false}
            showProfileButton={false}
            buttons={[
              { label: 'View Audit Trail Entry', onClick: handleViewAuditTrail },
              { label: 'Review', onClick: handleReviewClick },
              copyButton,
            ]}
          />

          {loading && <div>Loading audit trail...</div>}

          {snapshotLoading && <div>Loading...</div>}
          
          <ProcedureStatusInfo
            item_code_type={versionContent?.item_code_type}
            partNumber={versionContent?.partNumber}
            status={versionContent?.status}
            revision={versionContent?.revision}
            showDisabledCheckboxes={true}
            whenSaved={auditTrailData?.audit_date}
            checked={resolved.type === 'revision'}
          />

          <div style={{ padding: '0px 20px', marginTop: '50px' }} className="show-disabled-checkbox-enabled">
            {resolved.type === 'revision' && (
              <RenderRevisionNodes
                partRef={part_ref}
                procedureRevRef={procedure_rev_ref}
                preloadedData={sections}
                disabled={true}
                showCheckbox={true}
                checkboxState={{
                  allChildrenChecked: true,
                  allChildrenDisabled: true,
                }}
                auditTrailNewContentSnapshotUrl={new_content_snapshot}
                main_entry={isMainEntry}
              />
            )}
            {resolved.type === 'section' && resolved.section && (
              <SectionNode
                section={resolved.section}
                sectionIndex={resolved.sectionIndex}
                displaySectionOrdinal={resolved.displaySectionNumber}
                globalStepStart={resolved.globalStepStart}
                showCheckbox={true}
                showSectionCheckbox={true}
                disabled={false}
                allNodesData={sections}
                isSourceSection={actionToShow !== 'SECTION_COPIED'}
                checkboxState={checkboxState}
              />
            )}
            {resolved.type === 'step' && resolved.section && resolved.section.steps?.[resolved.stepIndex] && (
              <StepNode
                step={resolved.section.steps[resolved.stepIndex]}
                stepIndex={resolved.stepIndex}
                globalStepNumber={previewGlobalStepNumber}
                sectionIndex={resolved.sectionIndex}
                sectionHeading={`Section ${resolved.sectionIndex + 1}`}
                sectionId={resolved.section.tree_node_id}
                showCheckbox={true}
                disabled={false}
                allNodesData={sections}
                checkboxState={checkboxState}
              />
            )}
            {resolved.type === 'substep' && resolved.section && (() => {
              const step = resolved.section.steps[resolved.stepIndex]
              const substep = step?.substeps?.[resolved.substepIndex]
              return substep ? (
                <SubstepNode
                  substep={substep}
                  substepIndex={resolved.substepIndex}
                  stepIndex={resolved.stepIndex}
                  globalStepNumber={previewGlobalStepNumber}
                  sectionIndex={resolved.sectionIndex}
                  sectionHeading={`Section ${resolved.sectionIndex + 1}`}
                  stepHeading={`Step ${previewGlobalStepNumber}`}
                  sectionId={resolved.section.tree_node_id}
                  stepId={step.tree_node_id}
                  showCheckbox={true}
                  disabled={false}
                  allNodesData={sections}
                  checkboxState={checkboxState}
                  auditSubstepHeading={resolved.auditSubstepHeading}
                  auditSubstepBreadcrumb={resolved.auditSubstepSourceBreadcrumb}
                />
              ) : null
            })()}
            {resolved.childNode && (() => {
              const CHILD_NODE_MAP = {
                attachment: AttachmentNode,
                video: VideoNode,
                'rich-text': RichTextNode,
                canva: CanvasNode,
              }
              const ChildComponent = CHILD_NODE_MAP[resolved.type]
              if (!ChildComponent) return null
              const parentContext = {
                sectionIndex: resolved.sectionIndex,
                stepIndex: resolved.stepIndex,
                globalStepNumber: previewGlobalStepNumber,
                substepIndex: resolved.substepIndex >= 0 ? resolved.substepIndex : undefined,
                substepLetter:
                  resolved.substepIndex >= 0
                    ? String.fromCharCode(65 + resolved.substepIndex)
                    : undefined,
                auditSubstepBreadcrumbFull: resolved.auditSubstepSourceBreadcrumb,
              }
              return (
                <ChildComponent
                  node={resolved.childNode}
                  nodeNumber={resolved.globalChildNumber}
                  parentContext={parentContext}
                  nodeType={resolved.type}
                  showCheckbox={true}
                  disabled={false}
                  checkboxState={checkboxState}
                />
              )
            })()}
          </div>
        </div>
      }

      {showVersionHistory && (
        <VersionHistoryPopup
          onClose={() => setShowVersionHistory(false)}
          versionHistories={versionHistories || []}
          mode="edit"
          versionId={resolvedAuditVersionId}
          nodeId={
            auditTrailData?.version_content?.element_id ||
            auditTrailData?.version_content?.step_tree_node_id
          }
          nodeIdField="element_id"
          breadcrumbText={auditTrailData?.version_content?.breadcrumb_text}
          revisionTreeNodes={sections}
          procedureRevisionIdForNodeHistory={
            versionContent?.procedure_revision_id ??
            auditTrailData?.version_content?.procedure_revision_id
          }
          restrictNodeHistoryToProcedureRevision
          initialFilterType="versions_for_this_page"
          currentVersionId={resolvedAuditVersionId}
          exportCsvDisabled={versionHistoryLoading}
          emptyMessage="No version history available"
          onViewDetails={(row) => {
            const aid = row.audit_trail_id || row.audit_trail_id_ref
            if (!aid) return
            const path = getVersionHistoryAuditPreviewPath(
              aid,
              row.action || row.action_to_show,
            )
            if (!path) return
            setShowVersionHistory(false)
            navigate(path)
          }}
          isRowViewActionDisabled={(row) =>
            !(row.audit_trail_id || row.audit_trail_id_ref)
          }
        />
      )}

      {
        showCopyAndrelpace && <VersionHistoryCopyReplace data_for_copy_and_replace={data_for_copy_and_replace} onCancel={()=>setShowCopyAndrelpace(false)}/>
      }
    </>
  )
};


export default PreviewCommonComponent;